import { toast } from "$lib/components/organisms/toast/toast";
import type { ICableModel } from "$lib/models/ICableModel";
import { Cartesian2, Cartesian3, Cartographic, Math as CesiumMath, Entity } from "cesium";
import { selectedMapEntity } from "./cesium-entity-store";
import { get } from "svelte/store";
import { createCableArea } from "$lib/konvatools/drawings";

/**
 * Gets the calculated bottom width of the trench based on cable zones and padding. In meters
 * @param trenchData 
 * @returns 
 */
const getTrenchWidth = (trenchData: ICableModel.TrenchData) => {
    // Calculate bottom width of trench
    const trenchPadding = trenchData.horizontalPadding * 2;
    let cableZoneWidths: number[] = [];
    for(let cableZone of trenchData.cableZones){
        const cableWidthAccumulated = createCableArea(cableZone) / 1000;
        cableZoneWidths.push(cableWidthAccumulated);
    }
    const widestCableZoneWidth = Math.max(...cableZoneWidths);
    return trenchPadding + widestCableZoneWidth;
}

/**
 * Returns the calculated depth directly above where cables should be placed.
 * 
 * @param trenchData 
 * @returns 
 */
const getCoverThickness = (trenchData: ICableModel.TrenchData) => {
    const accumulatedRefillZones = trenchData.refillZones?.reduce((acc, zone) => acc + zone.depth, 0);
    /*const cableAreaMargin = trenchData.cableZones.reduce((acc, zone) => acc + zone.margin, 0);*/
     return accumulatedRefillZones ?? 0
}

const getCableAreaHeight = (cableZone: ICableModel.TrenchData["cableZones"][number]) => {
    if(cableZone?.objects.length === 0) return cableZone.margin;

    const objects = cableZone.objects.filter((object) => object.type === 'KABEL_HS' || object.type === 'KABEL_LS' || object.type === 'ROR' || object.type === 'JORD').map(obj => obj.data);
    const isPipe = (o: ICableModel.TrenchObjectTypes): o is ICableModel.PipeData => {
        return 'pipeBaseData' in o;
    }
    const isEcc = (o: ICableModel.TrenchObjectTypes): o is ICableModel.EccCable => {
        return 'cableConductorData' in o && o.cableConductorData.trenchObjectType === 'jordtråd';
    }
    const isCable = (o: ICableModel.TrenchObjectTypes): o is ICableModel.Cable => {
        return 'cableConductorData' in o && o.cableConductorData.trenchObjectType === 'kabel';
    }


    if(objects && objects.length){
        const biggestRadius = Math.max(...objects.map(r => {
            if(isPipe(r)){
                return r.pipeBaseData.diameter/1000/2
            }else if(isCable(r)) {
                return r.cableConductorData.diameter/1000/2;
            } else if(isEcc(r)){
                return r.cableConductorData.diameter/1000/2;
            } else {
                console.error('Biggest radius: Unknown object type in trench', r);
                return 0;
            }
        }));
        const biggestObjectHeight = biggestRadius * 2;
        const zoneMargin = cableZone.margin;

        return biggestObjectHeight + zoneMargin;
         
    } else {
        return 0;
    }
}

/**
 * Gets the total height of the trench from ground level to bottom of trench. In meters
 * @param trenchData 
 * @returns 
 */
const getTrenchHeight = (trenchData: ICableModel.TrenchData) => {
    if(!trenchData){
        toast({
            type: 'error',
            title: 'Feil ved beregning av grøftedybde',
            description: 'Ingen grøftedata funnet. Kan ikke beregne dybde.'
        });
        return 0;
    }
    const coverThickness = getCoverThickness(trenchData);
    const trenchHeight = coverThickness + trenchData.foundationZones.reduce((acc, zone) => acc + zone.depth, 0);
    let cableAreaHeight = 0;
    for(let cableZone of trenchData.cableZones) cableAreaHeight += getCableAreaHeight(cableZone);
    return trenchHeight + cableAreaHeight;
}

/**
 * 
 * @param trenchData 
 * @returns 
 */
const getTrenchTopWidth = (trenchData: ICableModel.TrenchData) => {
    /*const anglesBottom = (180 - (trenchData.trenchAngle * 2)) / 2;
    // Width of trapezoid top
    const topSideWidth = (getTrenchWidth(trenchData) - getTrenchHeight(trenchData)) * (Math.tan(anglesBottom) + Math.tan(anglesBottom)) / 10;
    return topSideWidth;*/

    const deg2rad = (d: number) => (d * Math.PI) / 180;

    const b = getTrenchWidth(trenchData);
    const h = getTrenchHeight(trenchData);
    const beta = deg2rad(trenchData.trenchAngle); // top interior angle

    // horizontal inset per side = h / |tan(beta)|
    // (tan(beta) is negative for obtuse top angles; take abs for geometry)
    const inset = h / Math.abs(Math.tan(beta));

    const topSideWidth = b + 2 * inset;

    // guard against numeric noise / impossible geometry
    return Math.max(0, topSideWidth);
}

const computeCircle = (radius: number) => {
    const positions = [];
    for (let i = 0; i < 360; i++) {
      const radians = CesiumMath.toRadians(i);
      positions.push(
        new Cartesian2(
          radius * Math.cos(radians),
          radius * Math.sin(radians)
        )
      );
    }
    return positions;
  };

  const getTrenchVolume = (trenchData: ICableModel.TrenchData, entity?: Entity) => {
    const depth = getTrenchHeight(trenchData);
    const topW = getTrenchTopWidth(trenchData);
    const botW = getTrenchWidth(trenchData);
    const area = depth * (topW + botW) / 2;
    let corridorPositions = get(selectedMapEntity)?.corridor?.positions?.getValue() as Cartesian3[] | undefined;
    if(entity){
        corridorPositions = entity?.corridor?.positions?.getValue() as Cartesian3[] | undefined;
    } 

    if(!corridorPositions || corridorPositions.length < 2) {
        toast({
            type: 'error',
            title: 'Feil ved beregning av grøftevolum',
            description: 'Ingen posisjoner funnet eller for få punkter. Kan ikke beregne volum.'
        })
        return {
        lengthM: undefined,
        volumeM3: undefined
    };
    }

    const cartographicPositions = corridorPositions?.map((pos) => Cartographic.fromCartesian(pos));
    const length = cartographicPositions.reduce((s,_,i)=> i? s+Cartesian3.distance(
      Cartesian3.fromRadians(cartographicPositions[i-1].longitude,cartographicPositions[i-1].latitude,0),
      Cartesian3.fromRadians(cartographicPositions[i].longitude,cartographicPositions[i].latitude,0)) : 0,0);

    return {
        lengthM: length,
        volumeM3: area*length
    };

    
  }

  /**
   * This function creates a trapezoid with mirrored sides. The trench shape. 
   */
  const createTrenchShape = (trenchData: ICableModel.TrenchData): Cartesian2[] => {
    // Trapezoid height
    const trenchHeight = getTrenchHeight(trenchData);
    // Trapezoid bottom width
    const trenchWidth = getTrenchWidth(trenchData);
    
    // Calculate the angles at the bottom of the trapezoid. Since the upper angles should be editable. Can be extended with angles that are not equal on both sides.
    const anglesBottom = (180 - (trenchData.trenchAngle * 2)) / 2;

    // Convert angles to radians. Use seperate variables in case angles differ. e.g. alphaRad and betaRad. 
    const alphaBetaRad = anglesBottom * (Math.PI / 180);

    // Start position bottom left corner
    const x0 = -(trenchWidth / 2); 
    const y0 = /*mode === '3D' ? -trenchHeight :*/ trenchHeight; 

    // Right bottom corner
    const x1 = x0 + trenchWidth; 
    const y1 = y0;

    // Right top corner
    // Math abs may not be the best solution if a trench is supposed to be "burrowed" or have a negative y-coordinate at one or two sides.
    // It is used temporarily as the y coordinate is always at ground level and to get correct calculation in both 2D and 3D, as the 2D grid has positive depth values, but polylineVolume needs negative values as they are relative to the polyline.
    const x2 = x0 - trenchHeight * Math.tan(alphaBetaRad);
    const y2 = Math.abs(y0) - trenchHeight;

    // Left top corner
    const x3 = x1 + trenchHeight * Math.tan(alphaBetaRad);
    const y3 = Math.abs(y0) - trenchHeight;

    const sides = [
        new Cartesian2(x0,y0),
        new Cartesian2(x1,y1),
        new Cartesian2(x3,y3),
        new Cartesian2(x2,y2)
    ];

    return sides;
}

/**
 * A function which creates arbitrary horizontal rulers in trench, calculated from trench shape.
 * 
 * @param heightToMeasure The height from bottom which should be shown as a horizontal ruler
 * @param subtractFromTop If true, the heightToMeasure value should be subtracted from the top not added from the bottom. Affects calculations
 * @param trenchData The trenchdata
 * @param mode 2D or 3D
 * @param cables Array of cables, if any
 *  
 * @returns Cartesian2[]
 */
const createTrenchMeasurements = ({
        heightToMeasure,
        subtractFromTop = false,
        trenchData,
        mode
    }: {
        heightToMeasure: number,
        subtractFromTop: boolean; 
        trenchData: ICableModel.TrenchData;
        mode: '2D' | '3D';
    }) => {

    let rulerHeightFromBottom = heightToMeasure;
    // Trench height
    const trenchHeight = getTrenchHeight(trenchData); 
    if(subtractFromTop){
        rulerHeightFromBottom = trenchHeight - heightToMeasure;
    }
    // Trapezoid bottom width
    const trenchWidth = getTrenchWidth(trenchData);
 
    // The upper angles are known. Calculate the angles at the bottom of the trapezoid. Since the upper angles should be editable. Can be extended with angles that are not equal on both sides.
    const anglesBottom = (180 - (trenchData.trenchAngle * 2)) / 2;

    // Convert angles to radians. Use seperate variables in case angles differ. e.g. alphaRad and betaRad. 
    const alphaBetaRad = anglesBottom * (Math.PI / 180);
    // const alphaRad = angleABottom * (Math.PI / 180);
    // const betaRad = angleBBottom * (Math.PI / 180);

    // Start position bottom left corner
    const x0 = -(trenchWidth / 2); 
    // Keep 3D mode in case we want to test measurements on 3D model
    const y0 = mode === '3D' ? -trenchHeight : trenchHeight; 

    // Right bottom corner
    const x1 = x0 + trenchWidth; 

    // Right top corner
    // Math abs may not be the best solution if a trench is supposed to be "burrowed" or have a negative y-coordinate at one or two sides.
    // It is used temporarily as the y coordinate is always at ground level and to get correct calculation in both 2D and 3D, as the 2D grid has positive depth values, but polylineVolume needs negative values as they are relative to the polyline.
    const x2 = x0 - rulerHeightFromBottom * Math.tan(alphaBetaRad);
    const y2 = Math.abs(y0) - rulerHeightFromBottom;

    // Left top corner
    const x3 = x1 + rulerHeightFromBottom * Math.tan(alphaBetaRad);
    const y3 = Math.abs(y0) - rulerHeightFromBottom;

    const sides = [
        new Cartesian2(x3,y3),
        new Cartesian2(x2,y2)
    ];

    return sides;
}

export {
    createTrenchShape,
    createTrenchMeasurements,
    computeCircle,
    getTrenchHeight,
    getTrenchTopWidth,
    getTrenchWidth,
    getTrenchVolume,
    getCableAreaHeight,
    getCoverThickness
}